import { Injectable, Inject } from '@angular/core';
import { from, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ProjectStore } from '../../utilities/stores/project/project-store';
import { ElementLog} from '../../utilities/stores/element-log/element-log.interface';
import { PveTemplate } from '../../utilities/export/pve-template.interface';
import { AreaTemplate } from '../../resources/area/area.interface';
import { BuildingTemplate } from '../../resources/building-template/building-template.interface';
import { ProjectElement } from '../../resources/element/project-element.interface';

@Injectable({
  providedIn: 'root'
})
export class ApiLegacyService {

  constructor(
    @Inject('sgProject') private sgProjectApiService: any,
    private projectStore: ProjectStore,
    private httpClient: HttpClient) { }

  patchElement(element) {
    const params = {
      id: element.projectId,
      paramId: null,
      secondParamId: null
    };

    // Quirky but the API seems to need it
    element.elementId = element._id;

    const body = {element: element, versionId: element.versionId};

    return this.sgProjectApiService.patchElement(params, body);
  }

  addElement(element) {
    const body = {element: element, versionId: element.versionId, _id: element.projectId};
    return this.sgProjectApiService.addElement(body).$promise;
  }

  updateModuleNoLoader(module) {
    const params = {
      id: module.projectId,
      paramId: module._id
    };

    const body = {module: module, versionId: module.versionId};

    return this.sgProjectApiService.updateModuleNoLoader(params, body);
  }

  updateSubModuleNoLoader(submodule) {
    const params = {
      id: submodule.projectId,
      paramId: submodule.moduleId,
      secondParamId: submodule._id
    };

    const body = {submodule: submodule, versionId: submodule.versionId};

    return this.sgProjectApiService.updateSubModuleNoLoader(params, body);
  }

  deactivateModule(module) {
    return this.sgProjectApiService.deactivateModule({id: module.projectId, paramId: module._id});
  }

  deactivateSubmodule(submodule) {
    return this.sgProjectApiService.deactivateSubModule({
      id: submodule.projectId, paramId: submodule.moduleId, secondParamId: submodule._id
    });
  }

  addElementComment(comment) {

    const elementComment = {
      _id: comment.projectId,
      versionId: comment.versionId,
      element: {_id: comment.elementId, elementId: comment.elementId, moduleId: comment.moduleId, submoduleId: comment.submoduleId},
      comment: comment
    };

    return this.sgProjectApiService.addElementComment(elementComment);
  }

  createModule(module) {
    return from(this.sgProjectApiService.createModule({
      _id: module.projectId,
      module: {name: module.name, isTechnicalType: module.isTechnicalType}
    }).$promise);
  }

  createSubmodule(submodule) {
    return from(this.sgProjectApiService.createSubModule({
      _id: submodule.projectId,
      paramId: submodule.moduleId,
      submodule: {name: submodule.name}
    }).$promise);
  }

  /**
   * Get element logs.
   *
   * @param element The element to get the logs for.
   * @returns Logs for the given elements.
   */
  public getElementLogs(element: ProjectElement): Observable<{ logs: ElementLog[] }> {
    const query = element.projectLevel === 5 ? `?constructionId=${element.constructionId}` : '';
    const params = [
      ['module', element.moduleId],
      ['submodule', element.submoduleId],
      ['element', element._id],
      [`logs${query}`]
    ];
    return this._get<{ logs: ElementLog[] }>(params);
  }

  public getBuildingTemplates(): Observable<BuildingTemplate[]> {
    const params = [
      ['buildingtemplate']
    ];
    return this._get<BuildingTemplate[]>(params, true);
  }


  public getPveTemplates(): Observable<PveTemplate[]> {
    const params = [
      ['pvetemplate']
    ];
    return this._get<PveTemplate[]>(params, true);
  }

  public getAreaTemplates(): Observable<AreaTemplate[]> {
    const params = [
      ['areatemplate']
    ];
    return this._get<AreaTemplate[]>(params, true);
  }

  /**
   * GET call.
   *
   * @todo Also create delete, patch and post methods.
   *
   * @param The Query params as an array of arrays.
   * @returns API response as an observable.
   */
  private _get<T>(params: any[][], excludeProject: boolean = false): Observable<any> {
    const call = new Promise(resolve => {
      this.httpClient.get<T>(this._generateUrl(params, excludeProject)).subscribe((response: any) => {
        resolve(response.data);
      });
    });
    return from(call);
  }

  /**
   * Generate API url from params.
   *
   * @returns API url.
   */
  private _generateUrl(route, excludeProject): string {
    let url = 'api';
    if (!excludeProject) {
      const projectId = this.projectStore.get()._id;
      url += `/project/${ projectId }`;
    }
    route.forEach(controller => {
      url += '/' + controller[0];
      url += controller[1] ? '/' + controller[1] : '';
    });
    return url;
  }
}
